import React, { useState } from "react";
import {
  ArrowLeft,
  Sun,
  Moon,
  Shield,
  AlertTriangle,
  Eye,
  Phone,
  Mail,
  FileText,
  Users,
  Lock,
  Flag,
  Scale,
  Globe,
} from "lucide-react";

const ChildSafetyStandards = () => {
  const [isDarkMode, setIsDarkMode] = useState(false);

  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
  };

  const reportingMethods = [
    {
      icon: <Flag className="w-6 h-6" />,
      title: "In-App Reporting",
      description:
        "Use the report feature available on any profile or content within the Homeegram app",
      action: "Report directly from the app",
    },
    {
      icon: <Mail className="w-6 h-6" />,
      title: "Email Report",
      description: "Send detailed reports to our dedicated child safety team",
      action: "abijith@homeeera.com",
    },
    {
      icon: <Phone className="w-6 h-6" />,
      title: "Emergency Contact",
      description:
        "For immediate threats to child safety requiring urgent response",
      action: "Contact local authorities first, then notify us",
    },
  ];

  const preventionMeasures = [
    {
      title: "Age Verification",
      description:
        "All users must be 18+ years old. We verify age during registration & may request additional verification documents.",
    },
    {
      title: "Professional Verification",
      description:
        "Enhanced verification process for construction professionals includes background checks & credential validation.",
    },
    {
      title: "Content Monitoring",
      description:
        "Automated & manual review systems monitor uploaded content for inappropriate material.",
    },
    {
      title: "Behavioral Analysis",
      description:
        "Advanced algorithms detect suspicious patterns & behaviors that may indicate CSAE risks.",
    },
    {
      title: "Secure Communication",
      description:
        "All communications are logged & can be reviewed if suspicious activity is reported.",
    },
    {
      title: "Regular Audits",
      description:
        "Continuous security audits & updates to our child safety protocols & detection systems.",
    },
  ];

  return (
    <div
      className={`min-h-screen transition-colors duration-300 font-['Poppins'] ${
        isDarkMode ? "bg-black text-white" : "bg-white text-gray-900"
      }`}
      style={{ fontFamily: "Poppins, sans-serif" }}
    >
      {/* Header */}
      <header
        className={`sticky top-0 z-50 transition-all duration-300 border-b ${
          isDarkMode
            ? "bg-black/95 backdrop-blur-md border-gray-800"
            : "bg-white/95 backdrop-blur-md border-gray-200"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => window.history.back()}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? "hover:bg-gray-800" : "hover:bg-gray-100"
                }`}
              >
                <ArrowLeft className="w-6 h-6" />
              </button>
              <div className="flex items-center space-x-3">
                <Shield className="w-8 h-8 text-blue-500" />
                <h1 className="text-xl font-semibold">Child Safety Standards</h1>
              </div>
            </div>

            <button
              onClick={toggleTheme}
              className={`p-2 rounded-full transition-colors ${
                isDarkMode
                  ? "bg-gray-800 text-[#FEF200] hover:bg-gray-700"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              {isDarkMode ? (
                <Sun className="w-5 h-5" />
              ) : (
                <Moon className="w-5 h-5" />
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <div
            className={`inline-flex items-center space-x-2 px-4 py-2 rounded-full mb-6 ${
              isDarkMode
                ? "bg-blue-900/20 border border-blue-800"
                : "bg-blue-50 border border-blue-200"
            }`}
          >
            <Shield className="w-5 h-5 text-blue-500" />
            <span className="text-sm font-medium text-blue-600">
              Child Safety & Anti-Exploitation (CSAE) Standards
            </span>
          </div>

          <h1 className="text-4xl sm:text-5xl font-semibold mb-6">
            Child Safety Standards
          </h1>
          <p
            className={`text-lg leading-relaxed max-w-3xl mx-auto ${
              isDarkMode ? "text-gray-300" : "text-gray-600"
            }`}
          >
            Homeegram is committed to maintaining the highest standards of child
            safety & has zero tolerance for Child Sexual Abuse &
            Exploitation (CSAE) content on our platform.
          </p>
        </div>

        {/* Zero Tolerance Statement */}
        <section className="mb-12">
          <div
            className={`rounded-2xl p-8 border-l-4 border-red-500 ${
              isDarkMode
                ? "bg-red-900/20 border-red-800"
                : "bg-red-50 border-red-200"
            }`}
          >
            <h2 className="text-2xl font-semibold mb-6 flex items-center">
              <AlertTriangle className="w-8 h-8 mr-3 text-red-500" />
              Zero Tolerance Policy
            </h2>
            <div className="space-y-4">
              <p className="text-lg font-semibold text-red-600">
                Homeegram has ZERO TOLERANCE for Child Sexual Abuse &
                Exploitation (CSAE) content.
              </p>
              <div className="space-y-3">
                <p>
                  <strong>Prohibited Content:</strong> Any content that depicts,
                  promotes, facilitates, or glorifies the sexual abuse or
                  exploitation of minors is strictly prohibited on Homeegram.
                </p>
                <p>
                  <strong>Immediate Action:</strong> Accounts sharing,
                  distributing, or attempting to access CSAE content will be
                  immediately suspended & reported to relevant authorities.
                </p>
                <p>
                  <strong>Legal Consequences:</strong> We cooperate fully with
                  law enforcement agencies & will pursue all available legal
                  remedies against violators.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Platform Context */}
        <section className="mb-12">
          <div
            className={`rounded-2xl p-8 ${
              isDarkMode
                ? "bg-gray-800 border border-gray-700"
                : "bg-gray-50 border border-gray-200"
            }`}
          >
            <h2 className="text-2xl font-semibold mb-6 flex items-center">
              <Users className="w-8 h-8 mr-3 text-[#FEF200]" />
              About Homeegram
            </h2>
            <p
              className={`mb-4 ${
                isDarkMode ? "text-gray-300" : "text-gray-600"
              }`}
            >
              India's First Unified Construction Social Networking Platform.
              designed exclusively for verified construction professionals. Our
              platform serves adults aged 18 & above in the construction &
              real estate industry.
            </p>
            <p className={`${isDarkMode ? "text-gray-300" : "text-gray-600"}`}>
              While our platform focuses on professional networking & does not
              target minors, we recognize our responsibility to maintain the
              highest standards of child safety & protection in all our
              operations.
            </p>
          </div>
        </section>

        {/* Reporting Mechanisms */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-8 flex items-center">
            <Flag className="w-8 h-8 mr-3 text-[#FEF200]" />
            Reporting CSAE Content or Behavior
          </h2>

          <div className="grid md:grid-cols-3 gap-6 mb-8">
            {reportingMethods.map((method, index) => (
              <div
                key={index}
                className={`rounded-xl p-6 text-center ${
                  isDarkMode
                    ? "bg-gray-800 border border-gray-700"
                    : "bg-white border border-gray-200"
                } hover:shadow-lg transition-all duration-300`}
              >
                <div className="w-16 h-16 bg-[#FEF200] text-black rounded-full flex items-center justify-center mx-auto mb-4">
                  {method.icon}
                </div>
                <h3 className="text-lg font-semibold mb-3">{method.title}</h3>
                <p
                  className={`text-sm mb-4 ${
                    isDarkMode ? "text-gray-300" : "text-gray-600"
                  }`}
                >
                  {method.description}
                </p>
                <div className="text-[#FEF200] font-medium text-sm">
                  {method.action}
                </div>
              </div>
            ))}
          </div>

          <div
            className={`p-6 rounded-lg ${
              isDarkMode ? "bg-gray-800" : "bg-yellow-50"
            }`}
          >
            <h3 className="text-lg font-semibold mb-3">
              What to Include in Your Report
            </h3>
            <ul className="space-y-2 text-sm">
              <li>
                • Specific details about the concerning content or behavior
              </li>
              <li>• Username or profile information of the reported user</li>
              <li>• Screenshots or evidence (if safe to obtain)</li>
              <li>• Date & time of the incident</li>
              <li>• Any additional context that may be helpful</li>
            </ul>
          </div>
        </section>

        {/* Response Procedures */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-8 flex items-center">
            <Eye className="w-8 h-8 mr-3 text-[#FEF200]" />
            Our Response Procedures
          </h2>

          <div className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div
                className={`p-6 rounded-xl ${
                  isDarkMode ? "bg-gray-800" : "bg-white border border-gray-200"
                }`}
              >
                <h3 className="text-lg font-semibold mb-3 text-red-500">
                  Immediate Actions
                </h3>
                <ul className="space-y-2 text-sm">
                  <li>• Immediate content removal & account suspension</li>
                  <li>• Preservation of evidence for law enforcement</li>
                  <li>
                    • Notification to relevant authorities within 24 hours
                  </li>
                  <li>
                    • Coordination with local & international law enforcement
                  </li>
                </ul>
              </div>

              <div
                className={`p-6 rounded-xl ${
                  isDarkMode ? "bg-gray-800" : "bg-white border border-gray-200"
                }`}
              >
                <h3 className="text-lg font-semibold mb-3 text-blue-500">
                  Investigation Process
                </h3>
                <ul className="space-y-2 text-sm">
                  <li>• Dedicated child safety team reviews all reports</li>
                  <li>• Forensic analysis of reported content & accounts</li>
                  <li>• Cooperation with specialized law enforcement units</li>
                  <li>• Follow-up actions based on investigation findings</li>
                </ul>
              </div>
            </div>

            <div
              className={`p-6 rounded-lg border-l-4 border-blue-500 ${
                isDarkMode ? "bg-blue-900/20" : "bg-blue-50"
              }`}
            >
              <h3 className="text-lg font-semibold mb-3">
                Law Enforcement Cooperation
              </h3>
              <p
                className={`text-sm ${
                  isDarkMode ? "text-gray-300" : "text-gray-600"
                }`}
              >
                Homeegram maintains partnerships with law enforcement agencies
                & child protection organizations. We provide full cooperation
                in investigations, including expedited response to legal
                requests & proactive reporting of suspected CSAE content to
                appropriate authorities including the National Center for
                Missing & Exploited Children (NCMEC) & local law enforcement
                agencies.
              </p>
            </div>
          </div>
        </section>

        {/* Prevention Measures */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-8 flex items-center">
            <Lock className="w-8 h-8 mr-3 text-[#FEF200]" />
            Prevention & Safety Measures
          </h2>

          <div className="grid md:grid-cols-2 gap-6">
            {preventionMeasures.map((measure, index) => (
              <div
                key={index}
                className={`p-6 rounded-xl ${
                  isDarkMode ? "bg-gray-800" : "bg-white border border-gray-200"
                } hover:shadow-lg transition-all duration-300`}
              >
                <h3 className="text-lg font-semibold mb-3">{measure.title}</h3>
                <p
                  className={`text-sm ${
                    isDarkMode ? "text-gray-300" : "text-gray-600"
                  }`}
                >
                  {measure.description}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Legal Compliance */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-8 flex items-center">
            <Scale className="w-8 h-8 mr-3 text-[#FEF200]" />
            Legal Compliance & Standards
          </h2>

          <div className="space-y-6">
            <div
              className={`p-6 rounded-xl ${
                isDarkMode ? "bg-gray-800" : "bg-white border border-gray-200"
              }`}
            >
              <h3 className="text-lg font-semibold mb-3">Regulatory Compliance</h3>
              <ul className="space-y-2 text-sm">
                <li>
                  • Compliance with Indian Information Technology Act, 2000
                </li>
                <li>
                  • Adherence to Protection of Children from Sexual Offences
                  (POCSO) Act, 2012
                </li>
                <li>
                  • Implementation of international best practices for child
                  safety
                </li>
                <li>
                  • Regular updates to policies based on evolving legal
                  requirements
                </li>
              </ul>
            </div>

            <div
              className={`p-6 rounded-xl ${
                isDarkMode ? "bg-gray-800" : "bg-white border border-gray-200"
              }`}
            >
              <h3 className="text-lg font-semibold mb-3">
                Reporting to Authorities
              </h3>
              <p
                className={`text-sm mb-3 ${
                  isDarkMode ? "text-gray-300" : "text-gray-600"
                }`}
              >
                Homeegram has established procedures for reporting confirmed
                Child Sexual Abuse Material (CSAM) to appropriate authorities:
              </p>
              <ul className="space-y-2 text-sm">
                <li>• Immediate reporting to local law enforcement agencies</li>
                <li>
                  • Coordination with National Crime Records Bureau (NCRB)
                </li>
                <li>
                  • Cooperation with international law enforcement through
                  proper channels
                </li>
                <li>• Mandatory reporting as required by applicable laws</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Contact & Resources */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-8 flex items-center">
            <Phone className="w-8 h-8 mr-3 text-[#FEF200]" />
            Emergency Resources & Contacts
          </h2>

          <div className="grid md:grid-cols-2 gap-6">
            <div
              className={`p-6 rounded-xl ${
                isDarkMode ? "bg-gray-800" : "bg-white border border-gray-200"
              }`}
            >
              <h3 className="text-lg font-semibold mb-3 text-red-500">
                Emergency Contacts
              </h3>
              <ul className="space-y-2 text-sm">
                <li>
                  <strong>National Emergency:</strong> 112
                </li>
                <li>
                  <strong>Childline India:</strong> 1098
                </li>
                <li>
                  <strong>Cyber Crime Helpline:</strong> 1930
                </li>
                <li>
                  <strong>Women/Child Helpline:</strong> 181
                </li>
              </ul>
            </div>

            <div
              className={`p-6 rounded-xl ${
                isDarkMode ? "bg-gray-800" : "bg-white border border-gray-200"
              }`}
            >
              <h3 className="text-lg font-semibold mb-3 text-blue-500">
                Homeegram Safety Team
              </h3>
              <ul className="space-y-2 text-sm">
                <li>
                  <strong>Child Safety Email:</strong> safety@homeegram.com
                </li>
                <li>
                  <strong>General Support:</strong> abijith@homeeera.com
                </li>
                <li>
                  <strong>Response Time:</strong> Within 24 hours
                </li>
                <li>
                  <strong>Emergency:</strong> Contact local authorities first
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* Related Policies */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-8 flex items-center">
            <FileText className="w-8 h-8 mr-3 text-[#FEF200]" />
            Related Policies & Information
          </h2>

          <div className="grid md:grid-cols-2 gap-6">
            <a
              href="/privacy-policy"
              className={`p-6 rounded-xl transition-all duration-300 hover:shadow-lg ${
                isDarkMode
                  ? "bg-gray-800 hover:bg-gray-700 border border-gray-700"
                  : "bg-white hover:bg-gray-50 border border-gray-200"
              }`}
            >
              <h3 className="text-lg font-semibold mb-3 text-[#FEF200]">
                Privacy Policy
              </h3>
              <p
                className={`text-sm ${
                  isDarkMode ? "text-gray-300" : "text-gray-600"
                }`}
              >
                Learn about our data protection measures, verification
                processes, & how we handle user information safely.
              </p>
            </a>

            <a
              href="/terms-and-conditions"
              className={`p-6 rounded-xl transition-all duration-300 hover:shadow-lg ${
                isDarkMode
                  ? "bg-gray-800 hover:bg-gray-700 border border-gray-700"
                  : "bg-white hover:bg-gray-50 border border-gray-200"
              }`}
            >
              <h3 className="text-lg font-semibold mb-3 text-[#FEF200]">
                Terms & Conditions
              </h3>
              <p
                className={`text-sm ${
                  isDarkMode ? "text-gray-300" : "text-gray-600"
                }`}
              >
                Review our platform rules, prohibited activities, & user
                responsibilities for maintaining a safe environment.
              </p>
            </a>
          </div>
        </section>

        {/* Footer Statement */}
        <div
          className={`p-8 rounded-2xl text-center ${
            isDarkMode
              ? "bg-gradient-to-r from-gray-800 to-gray-700"
              : "bg-gradient-to-r from-gray-50 to-gray-100"
          }`}
        >
          <Globe className="w-12 h-12 text-blue-500 mx-auto mb-4" />
          <h3 className="text-xl font-semibold mb-4">Our Commitment</h3>
          <p
            className={`max-w-3xl mx-auto mb-6 ${
              isDarkMode ? "text-gray-300" : "text-gray-600"
            }`}
          >
            Homeegram is committed to being a safe, professional platform for
            construction industry networking. We continuously update our safety
            measures & work with global partners to protect children &
            maintain the highest standards of online safety.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="mailto:safety@homeegram.com"
              className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-3 px-8 rounded-full transition-colors"
            >
              Report Safety Concern
            </a>
            <a
              href="/privacy-policy"
              className={`border-2 font-semibold py-3 px-8 rounded-full transition-colors ${
                isDarkMode
                  ? "border-gray-600 text-white hover:bg-gray-800"
                  : "border-gray-300 text-gray-700 hover:bg-gray-50"
              }`}
            >
              Read Privacy Policy
            </a>
          </div>
        </div>

        {/* Last Updated */}
        <div className="mt-8 text-center">
          <p
            className={`text-sm ${
              isDarkMode ? "text-gray-400" : "text-gray-500"
            }`}
          >
            Last Updated: August 15, 2025 | Version 1.0
          </p>
        </div>
      </main>

      <style jsx>{`
        html {
          scroll-behavior: smooth;
        }
      `}</style>
    </div>
  );
};

export default ChildSafetyStandards;
